import { useEffect } from "react"
import { reloadPage } from "./page-reload"
import { isOverlayBlocked, isOverlayIdle } from "./overlay-activity"
import { logger, LogContext } from "./logger"

// refresh on a schedule so long running browser sources don't build up leaked memory, sockets or timers
const SCHEDULED_REFRESH_MS = 60 * 60 * 1000
// once due, wait for the overlay to sit still this long before hiding it
const IDLE_QUIET_MS = 3000
const IDLE_CHECK_INTERVAL_MS = 1000
// a constantly busy stream still gets refreshed eventually
const MAX_IDLE_WAIT_MS = 15 * 60 * 1000

// the url builder is an interactive page, an hourly reload there would just throw away what the user typed
export const useOverlayFailsafes = ({ scheduledRefresh = true }: { scheduledRefresh?: boolean } = {}) => {
  useEffect(() => {
    // errors thrown outside react rendering, like in socket or timer callbacks, never reach the error boundary
    const handleError = (event: ErrorEvent) => {
      // resource load failures (a missing image) bubble here without an error object, they aren't crashes
      if (!event.error) return
      reloadPage('crash', event.error)
    }

    // rejected promises are usually network blips that apollo or the socket retry on their own, so just log them
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.warn(LogContext.OVERLAY, 'Unhandled promise rejection', undefined, event.reason)
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    let idleCheck: ReturnType<typeof setInterval> | undefined
    const refreshTimeout = scheduledRefresh ? setTimeout(() => {
      const dueAt = Date.now()
      idleCheck = setInterval(() => {
        if (!isOverlayIdle(IDLE_QUIET_MS) && Date.now() - dueAt < MAX_IDLE_WAIT_MS) return
        // past the max wait it still holds off for a win celebration, they're a few seconds and the moment matters
        if (isOverlayBlocked()) return
        clearInterval(idleCheck)
        reloadPage('scheduled')
      }, IDLE_CHECK_INTERVAL_MS)
    }, SCHEDULED_REFRESH_MS) : undefined

    // let the startup script in index.html know react is up, from here on these handlers own crash recovery
    window.__overlayMounted = true

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      clearTimeout(refreshTimeout)
      clearInterval(idleCheck)
    }
  }, [scheduledRefresh])
}
